import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement);

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalNews: 0,
    todayNews: 0,
    todayReach: 0,
    todayAdValue: 0,
    todayPrValue: 0,
    tonalityData: {},
    topPlatforms: [],
    topCategories: [],
    dailyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/dashboard/stats', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setStats(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Dashboard fetch error:', err.response?.data || err.message);
        setLoading(false);
      });
  }, []);

  const tonalityChartData = {
    labels: Object.keys(stats.tonalityData),
    datasets: [{
      data: Object.values(stats.tonalityData),
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#6B7280'],
    }],
  };

  const platformsChartData = {
    labels: stats.topPlatforms.map(p => p.platform),
    datasets: [{
      label: 'Articles',
      data: stats.topPlatforms.map(p => p.count),
      backgroundColor: '#3B82F6',
    }],
  };

  const CATEGORY_COLORS = ['#3B82F6', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#6B7280'];
  const dailyTrendCategories = Array.from(new Set(stats.dailyTrend.flatMap(d => Object.keys(d.categories))));
  const dailyTrendChartData = {
    labels: stats.dailyTrend.map(d => d.date),
    datasets: dailyTrendCategories.map((category, i) => ({
      label: category,
      data: stats.dailyTrend.map(d => d.categories[category] || 0),
      borderColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
      backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
      tension: 0.3,
    })),
  };

  return (
    <div className="w-full p-0">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 px-4">Dashboard - Daily Media Monitoring Summary</h2>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="w-full space-y-8">
          {/* Section 1: Today's Key Numbers */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Ringkasan Hari Ini</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Today's Articles</h3>
                  <div className="text-2xl lg:text-3xl font-bold text-blue-600">{stats.todayNews.toLocaleString()}</div>
                  <div className="mt-2 text-sm text-gray-600">Published today</div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Today's Media Reach</h3>
                  <div className="text-2xl lg:text-3xl font-bold text-green-600">{stats.todayReach.toLocaleString()}</div>
                  <div className="mt-2 text-sm text-gray-600">Total impressions</div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Total News Articles</h3>
                  <div className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalNews.toLocaleString()}</div>
                  <div className="mt-2 text-sm text-gray-600">All time</div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Today's Ad Value</h3>
                  <div className="text-2xl lg:text-3xl font-bold text-purple-600">Rp {stats.todayAdValue.toLocaleString()}</div>
                  <div className="mt-2 text-sm text-gray-600">Estimated value</div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Today's PR Value</h3>
                  <div className="text-2xl lg:text-3xl font-bold text-indigo-600">Rp {stats.todayPrValue.toLocaleString()}</div>
                  <div className="mt-2 text-sm text-gray-600">Public relations value</div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Trend over time */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Tren 7 Hari Terakhir</h3>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Article Volume Trend by Category</h3>
                {stats.dailyTrend.length > 0 ? (
                  <div className="h-56 w-full">
                    <Line data={dailyTrendChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-8">No data available</div>
                )}
              </div>
            </div>
          </section>

          {/* Section 3: Today's breakdown */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Rincian Hari Ini</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Today's Sentiment</h3>
                  {Object.keys(stats.tonalityData).length > 0 ? (
                    <div className="h-40 w-full flex items-center justify-center">
                      <Pie data={tonalityChartData} options={{ responsive: true, maintainAspectRatio: true }} height={160} />
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">No data available</div>
                  )}
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg lg:col-span-2">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Platforms Today</h3>
                  {stats.topPlatforms.length > 0 ? (
                    <div className="h-40 w-full flex items-center justify-center">
                      <Bar data={platformsChartData} options={{ responsive: true, maintainAspectRatio: true }} height={160} />
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">No data available</div>
                  )}
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg lg:col-span-3">
                <div className="p-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Categories Today</h3>
                  {stats.topCategories.length > 0 ? (
                    <div className="space-y-2">
                      {stats.topCategories.map((cat, index) => (
                        <div key={index} className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                              #{index + 1}
                            </div>
                            <span className="text-sm font-medium text-gray-900 truncate max-w-xs">{cat.category}</span>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            {cat.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">No data available</div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

